/**
 * Theme Context
 * 
 * Provides theme state management with localStorage persistence,
 * system preference detection, and Ant Design ConfigProvider integration.
 */

import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { ConfigProvider, App, message } from 'antd';
import type { ThemeMode, ThemePreference, ThemeContextValue } from '../types/theme';
import { lightTheme, darkTheme } from '../styles/theme';
import { markStart, markEnd } from '../utils/performance';

// Configure global message instance for theme support
message.config({
  prefixCls: 'ant-message',
  getContainer: () => document.body,
  top: 24,
  duration: 3,
  maxCount: 3,
});

// Storage keys
const THEME_STORAGE_KEY = 'theme-preference';
const STORAGE_NOTIFICATION_KEY = 'theme-storage-notification-shown';

// Create context
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Theme Provider Component
 * Wraps the application and provides theme functionality
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [themeMode, setThemeMode] = useState<ThemeMode>('auto');
  const [systemPreference, setSystemPreference] = useState<'light' | 'dark' | null>(null);
  const [isUserOverride, setIsUserOverride] = useState(false);
  const [storageAvailable, setStorageAvailable] = useState<'localStorage' | 'sessionStorage' | 'none'>('localStorage');

  /**
   * Check storage availability and show notification if needed
   */
  const checkStorageAvailability = (): 'localStorage' | 'sessionStorage' | 'none' => {
    // Try localStorage
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return 'localStorage';
    } catch (e) {
      // localStorage not available, try sessionStorage
      try {
        const testKey = '__storage_test__';
        sessionStorage.setItem(testKey, 'test');
        sessionStorage.removeItem(testKey);
        
        // Show notification only once per session
        const notificationShown = sessionStorage.getItem(STORAGE_NOTIFICATION_KEY);
        if (!notificationShown) {
          message.warning({
            content: 'Theme preferences will only persist during this session (localStorage is disabled)',
            duration: 5,
            key: 'storage-notification',
          });
          sessionStorage.setItem(STORAGE_NOTIFICATION_KEY, 'true');
        }
        
        return 'sessionStorage';
      } catch (e2) {
        // No storage available
        message.error({
          content: 'Browser storage is disabled. Theme changes will not persist.',
          duration: 5,
          key: 'storage-notification',
        });
        return 'none';
      }
    }
  };

  /**
   * Get value from available storage
   */
  const getStorageValue = (key: string): string | null => {
    if (storageAvailable === 'localStorage') {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    } else if (storageAvailable === 'sessionStorage') {
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  /**
   * Set value in available storage
   */
  const setStorageValue = (key: string, value: string): void => {
    if (storageAvailable === 'localStorage') {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        console.error('Failed to save to localStorage:', e);
      }
    } else if (storageAvailable === 'sessionStorage') {
      try {
        sessionStorage.setItem(key, value);
      } catch (e) {
        console.error('Failed to save to sessionStorage:', e);
      }
    }
  };

  /**
   * Detect system color scheme preference
   */
  const detectSystemPreference = (): 'light' | 'dark' => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };

  /**
   * Calculate effective theme based on mode and system preference
   */
  const getEffectiveTheme = (mode: ThemeMode, sysPref: 'light' | 'dark' | null): 'light' | 'dark' => {
    if (mode === 'auto') {
      return sysPref || 'light';
    }
    return mode as 'light' | 'dark';
  };

  /**
   * Initialize theme on mount
   */
  useEffect(() => {
    // Check storage availability
    const storage = checkStorageAvailability();
    setStorageAvailable(storage);

    // Detect system preference
    const sysPref = detectSystemPreference();
    setSystemPreference(sysPref);

    // Load saved theme or use system preference
    const savedMode = getStorageValue(THEME_STORAGE_KEY) as ThemeMode | null;
    if (savedMode && ['light', 'dark', 'auto'].includes(savedMode)) {
      setThemeMode(savedMode);
      setIsUserOverride(savedMode !== 'auto');
    } else {
      setThemeMode('auto');
      setIsUserOverride(false);
    }

    setIsLoading(false);

    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      const newPreference = e.matches ? 'dark' : 'light';
      setSystemPreference(newPreference);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  /**
   * Sync data-theme attribute with effective theme
   * This enables CSS [data-theme='dark'] selectors to work
   */
  useEffect(() => {
    const effectiveTheme = getEffectiveTheme(themeMode, systemPreference);
    document.documentElement.setAttribute('data-theme', effectiveTheme);
  }, [themeMode, systemPreference]);

  /**
   * Toggle theme (cycles through auto → light → dark → auto)
   */
  const toggleTheme = () => {
    const start = markStart('theme-toggle');
    const nextMode: ThemeMode = themeMode === 'auto' ? 'light' : themeMode === 'light' ? 'dark' : 'auto';
    setThemeMode(nextMode);
    setIsUserOverride(nextMode !== 'auto');
    setStorageValue(THEME_STORAGE_KEY, nextMode);
    markEnd('theme-toggle', start);
  };

  /**
   * Set specific theme mode
   */
  const setTheme = (mode: ThemeMode) => {
    const start = markStart('theme-set');
    setThemeMode(mode);
    setIsUserOverride(mode !== 'auto');
    setStorageValue(THEME_STORAGE_KEY, mode);
    markEnd('theme-set', start);
  };

  /**
   * Build theme preference object
   */
  const preference = useMemo<ThemePreference>(() => {
    const effectiveTheme = getEffectiveTheme(themeMode, systemPreference);
    return {
      mode: themeMode,
      isUserOverride,
      systemPreference,
      effectiveTheme,
    };
  }, [themeMode, isUserOverride, systemPreference]);

  /**
   * Select appropriate Ant Design theme
   */
  const antdTheme = useMemo(() => {
    return preference.effectiveTheme === 'dark' ? darkTheme : lightTheme;
  }, [preference.effectiveTheme]);

  /**
   * Context value
   */
  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      preference,
      toggleTheme,
      setTheme,
      isLoading,
    }),
    [preference, isLoading]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={antdTheme}>
        <App>
          <ThemeAwareAppWrapper preference={preference}>
            {children}
          </ThemeAwareAppWrapper>
        </App>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

/**
 * Internal wrapper component for theme-aware app initialization
 * This ensures message and notification inherit the correct theme
 */
function ThemeAwareAppWrapper({ 
  children, 
  preference 
}: { 
  children: ReactNode;
  preference: ThemePreference;
}) {
  // Update message appearance based on theme changes
  useEffect(() => {
    // Force re-render of message instances by updating key
    // This is a workaround to ensure message respects theme changes
    const isDark = preference.effectiveTheme === 'dark';
    
    // Add class to body for additional theme styling support
    if (isDark) {
      document.body.classList.add('dark-theme-active');
      document.body.classList.remove('light-theme-active');
    } else {
      document.body.classList.add('light-theme-active');
      document.body.classList.remove('dark-theme-active');
    }
    
    return () => {
      document.body.classList.remove('dark-theme-active', 'light-theme-active');
    };
  }, [preference.effectiveTheme]);

  return <>{children}</>;
}

/**
 * Hook to access theme context
 * Must be used within ThemeProvider
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
